import {
  AmbientLight,
  Color,
  DirectionalLight,
  Fog,
  HemisphereLight,
  MathUtils,
  Scene,
} from "three";

import type { RenderVector3 } from "./types.ts";
import type { AtmosphericSkyConfig } from "./sky.ts";

export type EnvironmentSeason = "spring" | "summer" | "autumn" | "winter";

export interface EnvironmentPresentation {
  readonly season: EnvironmentSeason;
  readonly timeOfDayHours: number;
  readonly frozen: boolean;
  readonly surfaceWetness01?: number;
}

export interface EnvironmentPalette {
  readonly sky: number;
  readonly fog: number;
  readonly hemisphereSky: number;
  readonly hemisphereGround: number;
  readonly sun: number;
  readonly fill: number;
  readonly hemisphereIntensity: number;
  readonly sunIntensity: number;
  readonly fillIntensity: number;
}

export interface EnvironmentSeasonProfile {
  readonly sky: number;
  readonly fog: number;
  readonly hemisphereSky: number;
  readonly hemisphereGround: number;
  readonly sun: number;
  readonly fill: number;
  readonly paletteBlend: number;
  readonly lightBlend: number;
  readonly fogDistanceScale: number;
  readonly exposureScale: number;
  readonly sunHeightScale: number;
}

export interface EnvironmentRenderConfig {
  readonly dayLengthSeconds: number;
  readonly scrubStepHours: number;
  readonly fogNearMeters: number;
  readonly fogFarDayMeters: number;
  readonly fogFarNightMeters: number;
  readonly flightFogDistanceScale: number;
  readonly sunDistanceMeters: number;
  readonly shadowFocusHeightMeters: number;
  readonly exposureDay: number;
  readonly exposureDusk: number;
  readonly exposureNight: number;
  readonly day: EnvironmentPalette;
  readonly dusk: EnvironmentPalette;
  readonly night: EnvironmentPalette;
  readonly seasons: Readonly<Record<EnvironmentSeason, EnvironmentSeasonProfile>>;
  readonly atmosphere: AtmosphericSkyConfig;
}

export interface EnvironmentFrameState extends EnvironmentPresentation {
  readonly surfaceWetness01: number;
  readonly nightFactor: number;
  readonly duskFactor: number;
  readonly exposure: number;
  readonly fogFarMeters: number;
}

export interface EnvironmentSceneController {
  set(presentation: Readonly<EnvironmentPresentation>): void;
  setAerialVisibilityEnabled(enabled: boolean): void;
  setFogFarDayMeters(
    groundedFogFarDayMeters: number,
    flightFogFarDayMeters: number,
  ): void;
  scrub(deltaHours: number): void;
  toggleFrozen(): boolean;
  update(deltaSeconds: number, focus: Readonly<RenderVector3>): EnvironmentFrameState;
  getState(): EnvironmentFrameState;
  dispose(): void;
}

function normalizedHour(hours: number): number {
  return ((hours % 24) + 24) % 24;
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const normalized = MathUtils.clamp(
    (value - edge0) / (edge1 - edge0),
    0,
    1,
  );
  return normalized * normalized * (3 - 2 * normalized);
}

export function environmentFactors(timeOfDayHours: number): Readonly<{
  duskFactor: number;
  nightFactor: number;
}> {
  const hour = normalizedHour(timeOfDayHours);
  const eveningDusk = smoothstep(17.5, 20.5, hour) *
    (1 - smoothstep(21.5, 23, hour));
  const morningDusk = (1 - smoothstep(4.5, 6.5, hour)) *
    smoothstep(2.5, 4.5, hour);
  const nightFactor = hour >= 12
    ? smoothstep(19.5, 23, hour)
    : 1 - smoothstep(4.5, 7, hour);
  return {
    duskFactor: Math.max(eveningDusk, morningDusk),
    nightFactor,
  };
}

function mixColor(
  day: number,
  dusk: number,
  night: number,
  duskFactor: number,
  nightFactor: number,
): Color {
  return new Color(day)
    .lerp(new Color(dusk), duskFactor)
    .lerp(new Color(night), nightFactor);
}

function mixNumber(
  day: number,
  dusk: number,
  night: number,
  duskFactor: number,
  nightFactor: number,
): number {
  return MathUtils.lerp(
    MathUtils.lerp(day, dusk, duskFactor),
    night,
    nightFactor,
  );
}

export function createEnvironmentSceneController(
  scene: Scene,
  hemisphere: HemisphereLight,
  direction: DirectionalLight,
  fill: AmbientLight,
  config: Readonly<EnvironmentRenderConfig>,
  onFrame?: (state: Readonly<EnvironmentFrameState>) => void,
): EnvironmentSceneController {
  let presentation: Required<EnvironmentPresentation> = {
    season: "summer",
    timeOfDayHours: 10,
    frozen: false,
    surfaceWetness01: 0,
  };
  let state: EnvironmentFrameState = {
    ...presentation,
    duskFactor: 0,
    nightFactor: 0,
    exposure: config.exposureDay,
    fogFarMeters: config.fogFarDayMeters,
  };
  let groundedFogFarDayMeters = config.fogFarDayMeters;
  let flightFogFarDayMeters =
    config.fogFarDayMeters * config.flightFogDistanceScale;
  let aerialVisibilityEnabled = false;
  let disposed = false;

  function apply(focus: Readonly<RenderVector3>): EnvironmentFrameState {
    const factors = environmentFactors(presentation.timeOfDayHours);
    const season = config.seasons[presentation.season];
    const seasonPaletteBlend =
      season.paletteBlend * (1 - factors.nightFactor * 0.78);
    const seasonLightBlend =
      season.lightBlend * (1 - factors.nightFactor * 0.45);
    const palette = {
      sky: mixColor(
        config.day.sky,
        config.dusk.sky,
        config.night.sky,
        factors.duskFactor,
        factors.nightFactor,
      ),
      fog: mixColor(
        config.day.fog,
        config.dusk.fog,
        config.night.fog,
        factors.duskFactor,
        factors.nightFactor,
      ),
      hemisphereSky: mixColor(
        config.day.hemisphereSky,
        config.dusk.hemisphereSky,
        config.night.hemisphereSky,
        factors.duskFactor,
        factors.nightFactor,
      ),
      hemisphereGround: mixColor(
        config.day.hemisphereGround,
        config.dusk.hemisphereGround,
        config.night.hemisphereGround,
        factors.duskFactor,
        factors.nightFactor,
      ),
      sun: mixColor(
        config.day.sun,
        config.dusk.sun,
        config.night.sun,
        factors.duskFactor,
        factors.nightFactor,
      ),
      fill: mixColor(
        config.day.fill,
        config.dusk.fill,
        config.night.fill,
        factors.duskFactor,
        factors.nightFactor,
      ),
    };
    palette.sky.lerp(new Color(season.sky), seasonPaletteBlend);
    palette.fog.lerp(new Color(season.fog), seasonPaletteBlend);
    palette.hemisphereSky.lerp(
      new Color(season.hemisphereSky),
      seasonLightBlend,
    );
    palette.hemisphereGround.lerp(
      new Color(season.hemisphereGround),
      seasonLightBlend,
    );
    palette.sun.lerp(new Color(season.sun), seasonLightBlend);
    palette.fill.lerp(new Color(season.fill), seasonLightBlend);
    scene.background = palette.sky;
    const selectedFogFarDayMeters = aerialVisibilityEnabled
      ? flightFogFarDayMeters
      : groundedFogFarDayMeters;
    const fogFarMeters =
      MathUtils.lerp(
        selectedFogFarDayMeters,
        selectedFogFarDayMeters *
          config.fogFarNightMeters / config.fogFarDayMeters,
        factors.nightFactor,
      ) * season.fogDistanceScale;
    if (scene.fog instanceof Fog) {
      scene.fog.color.copy(palette.fog);
      scene.fog.near = config.fogNearMeters;
      scene.fog.far = fogFarMeters;
    }
    hemisphere.color.copy(palette.hemisphereSky);
    hemisphere.groundColor.copy(palette.hemisphereGround);
    hemisphere.intensity = mixNumber(
      config.day.hemisphereIntensity,
      config.dusk.hemisphereIntensity,
      config.night.hemisphereIntensity,
      factors.duskFactor,
      factors.nightFactor,
    );
    direction.color.copy(palette.sun);
    direction.intensity = mixNumber(
      config.day.sunIntensity,
      config.dusk.sunIntensity,
      config.night.sunIntensity,
      factors.duskFactor,
      factors.nightFactor,
    );
    fill.color.copy(palette.fill);
    fill.intensity = mixNumber(
      config.day.fillIntensity,
      config.dusk.fillIntensity,
      config.night.fillIntensity,
      factors.duskFactor,
      factors.nightFactor,
    );

    const solarRadians =
      ((normalizedHour(presentation.timeOfDayHours) - 6) / 24) * Math.PI * 2;
    const horizontal = Math.cos(solarRadians);
    const vertical =
      Math.max(0.1, Math.sin(solarRadians)) * season.sunHeightScale;
    direction.target.position.set(
      focus.x,
      focus.y + config.shadowFocusHeightMeters,
      focus.z,
    );
    direction.position.set(
      focus.x - horizontal * config.sunDistanceMeters,
      focus.y + vertical * config.sunDistanceMeters,
      focus.z - Math.sin(solarRadians * 0.63) * config.sunDistanceMeters,
    );
    direction.target.updateMatrixWorld();

    state = {
      ...presentation,
      ...factors,
      exposure:
        mixNumber(
          config.exposureDay,
          config.exposureDusk,
          config.exposureNight,
          factors.duskFactor,
          factors.nightFactor,
        ) * season.exposureScale,
      fogFarMeters,
    };
    onFrame?.(state);
    return state;
  }

  return {
    set(next): void {
      if (disposed) {
        throw new Error("Environment controller is disposed");
      }
      presentation = {
        season: next.season,
        timeOfDayHours: normalizedHour(next.timeOfDayHours),
        frozen: next.frozen,
        surfaceWetness01: MathUtils.clamp(
          Number.isFinite(next.surfaceWetness01)
            ? next.surfaceWetness01 ?? 0
            : presentation.surfaceWetness01,
          0,
          1,
        ),
      };
    },
    setAerialVisibilityEnabled(enabled): void {
      aerialVisibilityEnabled = enabled;
    },
    setFogFarDayMeters(
      nextGroundedFogFarDayMeters,
      nextFlightFogFarDayMeters,
    ): void {
      for (const [label, value] of [
        ["grounded", nextGroundedFogFarDayMeters],
        ["flight", nextFlightFogFarDayMeters],
      ] as const) {
        if (!Number.isFinite(value) || value <= config.fogNearMeters) {
          throw new Error(
            `${label} fog distance must exceed the fog near distance`,
          );
        }
      }
      groundedFogFarDayMeters = nextGroundedFogFarDayMeters;
      flightFogFarDayMeters = nextFlightFogFarDayMeters;
    },
    scrub(deltaHours): void {
      presentation = {
        ...presentation,
        timeOfDayHours: normalizedHour(
          presentation.timeOfDayHours + deltaHours,
        ),
        frozen: true,
      };
    },
    toggleFrozen(): boolean {
      presentation = {
        ...presentation,
        frozen: !presentation.frozen,
      };
      return presentation.frozen;
    },
    update(deltaSeconds, focus): EnvironmentFrameState {
      if (!presentation.frozen && deltaSeconds > 0) {
        presentation = {
          ...presentation,
          timeOfDayHours: normalizedHour(
            presentation.timeOfDayHours +
              deltaSeconds * 24 / config.dayLengthSeconds,
          ),
        };
      }
      return apply(focus);
    },
    getState(): EnvironmentFrameState {
      return { ...state };
    },
    dispose(): void {
      disposed = true;
    },
  };
}
